import { useState } from "react";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/** Slim banner prompting new accounts to confirm their email (soft gate, full access either way).
 *  A code is emailed automatically at registration; this lets people enter or resend it. */
export default function EmailConfirmBanner() {
  const { user, refreshUser } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState("");
  if (!user || user.email_verified !== false) return null;

  const run = async (key, fn, msg) => {
    setBusy(key);
    try {
      await fn();
      if (msg) toast.success(msg);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mb-8 border border-amber-200 bg-amber-50/70 rounded-md p-4 sm:p-5" data-testid="email-confirm-banner">
      <div className="flex items-start gap-3">
        <MailCheck className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-zinc-950">Confirm your email address</p>
          <p className="text-xs text-zinc-600 mt-1 leading-relaxed">
            We emailed a 6-digit code to <span className="font-medium text-zinc-900">{user.email}</span> when you joined.
            Entering it confirms your address so alerts and application emails always reach you.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" maxLength={6}
              inputMode="numeric" data-testid="banner-email-code-input"
              className="h-10 px-3 w-36 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <button disabled={busy === "confirm" || code.trim().length !== 6}
              onClick={() => run("confirm", async () => {
                await api.post("/auth/verify/email/confirm", { code: code.trim() });
                await refreshUser();
              }, "Email confirmed. Thank you.")}
              data-testid="banner-email-confirm"
              className="h-10 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors duration-200">
              {busy === "confirm" ? "Checking…" : "Confirm"}
            </button>
            <button disabled={busy === "resend"}
              onClick={() => run("resend", () => api.post("/auth/verify/email/send"), "Fresh code sent, check your inbox.")}
              data-testid="banner-email-resend"
              className="h-10 px-4 border border-zinc-300 bg-white text-xs font-semibold rounded-md hover:border-zinc-500 disabled:opacity-50 transition-colors duration-200">
              Resend code
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
